import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getSelectedEntity, selectFilterHistory, selectLabeledHistory } from 'store/Filters/selectors';
import { removeFilter, storeDashboardFilter } from 'store/Filters/actions';
import { CLOSED_FILTERS, CONFIRMED_FLAG_FILTERS } from 'store/Filters/constants'; import PropTypes from 'prop-types';
import {
  CloseIcon, TagContainer, TagLabel, FilterTagContainer, Label
} from './styled';
import moment from 'moment';
import { selectCustomAttributes } from 'store/CustomAttributes/selectors';

const { confirmed: CONFIRMED_FILTER } = CONFIRMED_FLAG_FILTERS;
const { closed: CLOSED_FILTER } = CLOSED_FILTERS;

const getDashboardMirrorKey = (filter) => {
  if (!filter) return null;
  if (filter.name === CONFIRMED_FILTER.name && filter.op === CONFIRMED_FILTER.op && String(filter.val) === CONFIRMED_FILTER.val) return 'confirmed';
  if (filter.name === CLOSED_FILTER.name && filter.op === CLOSED_FILTER.op && String(filter.val) === CLOSED_FILTER.val) return 'closed';
  return null;
};

export const Tag = ({
  showDelete, value, onDelete, type, valSelected, filterKey, customAttributes, op
}) => {
  const selectedEntity = useSelector(getSelectedEntity);
  const isDate = type === 'val' && filterKey.toLowerCase().includes('date') && op !== 'range';
  const isCustomField = type === 'key' && filterKey.toLowerCase().includes('custom_field');
  const isPermission = type === 'val' && filterKey.toLowerCase().includes('permission');
  const isStatus = type === 'val' && filterKey.toLowerCase().includes('status') && (selectedEntity === 'workspaces');
  const permissionLabel = value === 'True' ? 'Locked' : 'Unlocked';
  const statusLabel = value === 'True' ? 'Active' : 'Archived';
  const label = isCustomField ? customAttributes.find((cf) => cf.field_name === value.split('->')[1]).field_display_name : '';
  const isOneOfVal = type === 'val' && (op === 'is one of');
  const rangeOp = type === 'val' && (op === 'range');
  const isNotOneOfVal = type === 'val' && (op === 'is not one of');

  const valueParsed = () => {
    if (isDate) return moment(value).format('L');
    else if (isCustomField) return label;
    else if (isOneOfVal || isNotOneOfVal) return value.split(',');
    else if (isPermission) return permissionLabel;
    else if (isStatus) return statusLabel;
    else if (rangeOp) {
      const [start, end] = value.split(',');
      const dateString = `${moment(start).format('ddd MMM D YYYY')} - ${moment(end).format('ddd MMM D YYYY')}`;
      return dateString;
    } else return value;
  };

  if (!value) return null;
  return (
    <TagContainer type={ type } valSelected={ valSelected } multiplesValues={ isOneOfVal || isNotOneOfVal }>
      { (isOneOfVal || isNotOneOfVal)
        ? valueParsed()?.map((v, i) => <Label key={ `filter_value__${value}_${i}` }>{ v }</Label>)
        : <TagLabel type={ type }>
            {valueParsed()}
          </TagLabel>
      }

      { showDelete && <CloseIcon onClick={ onDelete } />}
    </TagContainer>
  );
};

const NO_WILDCARD_KEYS = ['last_execution_tool'];

export const FilterTags = ({ onSearch }) => {
  const dispatch = useDispatch();
  const selectedEntity = useSelector(getSelectedEntity);
  const filterHistory = useSelector((state) => selectFilterHistory(selectedEntity, state));
  const labeledFilters = useSelector((state) => selectLabeledHistory(selectedEntity, state));
  const customAttributes = useSelector(selectCustomAttributes);
  const deleteTag = (item) => {
    const specialOperator = (item.op === 'is one of') || (item.op === 'is not one of');
    const statusTraceRange = item.name === 'Status Trace Date';
    const values = ['risk-accepted', 'vulnerability_web'];
    const checkVal = (f, item) => values.includes(f.val) ? (String(f.val).replace(/[-_]/g, ' ')?.toLowerCase() === String(item.val).toLowerCase()) : (String(f.val).toLowerCase() === String(item.val).toLowerCase());
    const originalFilter = filterHistory.filter((f) => {
      if (statusTraceRange) {
        return (f.name === item.originalName) && (f.op === item.originalOp) && (f.val.name === 'change_date') && (item.val.includes(f.val.val));
      } else if (specialOperator) {
        return (f.name === item.originalName) && (f.op === item.originalOp) && (item.originalOpVal ? (f.val.op === item.originalOpVal) : (f.op === item.originalOp));
      } else {
        if (item.originalOpVal) {
          return (f.name === item.originalName) && (f.op === item.originalOp) && (f.val.op === item.originalOpVal) && ((item.originalOpVal === 'ilike') ? (f.val.val === `%${item.val}%`) : (String(f.val.val).toLowerCase() === String(item.val).toLowerCase()));
        } else {
          const ilikePredicate = NO_WILDCARD_KEYS.includes(item.originalName) ? (f.val === item.val) : (f.val === `%${item.val}%`);
          return (f.name === item.originalName) && (f.op === item.originalOp) && ((item.originalOp === 'ilike') ? ilikePredicate : checkVal(f, item));
        }
      }
    });
    const filterToRemove = originalFilter[0];
    if (filterToRemove) {
      dispatch(removeFilter(selectedEntity, filterToRemove, specialOperator));
      if (selectedEntity === 'vulns') {
        const mirrorKey = getDashboardMirrorKey(filterToRemove);
        if (mirrorKey) dispatch(storeDashboardFilter(mirrorKey, false));
      }
    }
    onSearch();
  };

  return (
    labeledFilters.map((filter, i) => (
      <FilterTagContainer key={ `${JSON.stringify(filter)}_${i}` }>
        { filter.name.includes('custom_fields') && <Tag type="mainKey" filterKey={ filter.name.split('->')[0] } value="Custom Attribute" /> }
        <Tag type="key" filterKey={ filter.name } value={ filter.name } customAttributes={ customAttributes } />
        <Tag type="op" filterKey={ filter.name } value={ filter.op } valSelected={ filter.val } />
        <Tag type="val" filterKey={ filter.name } value={ filter.val } showDelete onDelete={ () => deleteTag(filter) } op={ filter.op } />
      </FilterTagContainer>
    )));
};

export const DraftTag = ({ draftTag }) => (
  <>
    <Tag type="key" value={ draftTag.name } />
    <Tag type="op" value={ draftTag.op } />
  </>
);

Tag.propTypes = {
  showDelete: PropTypes.bool,
  type: PropTypes.string.isRequired,
  onDelete: PropTypes.func,
  value: PropTypes.string,
  filterKey: PropTypes.string
};

Tag.defaultProps = {
  onDelete: null,
  showDelete: false,
  value: '',
  filterKey: ''
};

DraftTag.propTypes = {
  draftTag: PropTypes.object.isRequired
};
